import React, { useState, useEffect } from 'react';
import Header from '../../sharedcomponents/header/header';
import { fetchApi } from '../../services/api';
import { buildCreateUserPayload, buildUpdateUserPayload, buildDeleteUserPayload, buildUserGridEntry } from './payloads/UserMasterPayload';
import { buildAuditLogPayload } from './payloads/AuditLogPayload';
import kmsEncryption from '../../services/kmsEncryption';
import eyeIcon from '../../assets/images/eye.svg';
import editIcon from '../../assets/images/edit.svg';

import * as XLSX from 'xlsx';
import { sanitizeDataObject, containsFormulaInjection } from '../../utils/csvSanitizer';
import './css/masters.css';

const userTypeOptions = ['Godrej Employee', 'Supplier'];

const UserMaster = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [editIndex, setEditIndex] = useState(null);
  const [formData, setFormData] = useState({
    userName: '', userType: '', role: '', division: '', location: '',
    bpCode: '', emailId: '', mobileNumber: '', status: true
  });
  const [apiMessage, setApiMessage] = useState({ text: '', type: '' });
  const [mobileError, setMobileError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const pageSize = 7;

  useEffect(() => {
    fetchUsers();
    fetchRoleOptions();
  }, []);

  const fetchRoleOptions = async () => {
    try {
      const response = await fetchApi('/api/RoleMaster/GetRoles', { method: 'GET' });
      let rolesData = [];
      if (Array.isArray(response)) rolesData = response;
      else if (response.data && Array.isArray(response.data)) rolesData = response.data;
      else if (response.result && Array.isArray(response.result)) rolesData = response.result;
      else if (response.roles && Array.isArray(response.roles)) rolesData = response.roles;

      const activeRoles = rolesData
        .filter(role => role.Status === 'Active')
        .map(role => ({ code: role.RoleCode, description: role.RoleDescription }));
      setRoleOptions(activeRoles);
    } catch (error) {
      console.error('[UserMaster] GetRoles Error:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetchApi('/api/UserMaster/GetUsers', { method: 'GET' });

      let usersData = [];
      if (Array.isArray(response)) usersData = response;
      else if (response.data && Array.isArray(response.data)) usersData = response.data;
      else if (response.result && Array.isArray(response.result)) usersData = response.result;
      else if (response.users && Array.isArray(response.users)) usersData = response.users;

      // Process each user - only decrypt emails if encrypted
      const mappedUsers = await Promise.all(usersData.map(async (user) => {
        // Handle display name - UserName should never be encrypted
        let displayUserName = user.UserName;
        
        // If UserName appears to be encrypted (legacy bad data), don't use it
        if (!displayUserName || displayUserName.trim() === '' || kmsEncryption.isEncrypted(displayUserName)) {
          // Use EmailID instead (decrypt if needed)
          if (kmsEncryption.isEncrypted(user.EmailID)) {
            try {
              const decryptedEmail = await kmsEncryption.decryptField(user.EmailID);
              displayUserName = decryptedEmail?.includes('@') ? decryptedEmail.split('@')[0] : decryptedEmail || 'Unknown User';
            } catch (error) {
              console.warn('[UserMaster] Failed to decrypt EmailID:', error);
              displayUserName = 'Unknown User';
            }
          } else if (user.EmailID?.includes('@')) {
            displayUserName = user.EmailID.split('@')[0];
          } else {
            displayUserName = 'Unknown User';
          }
        }

        // Handle CreatedBy display (usually emails)
        let displayCreatedBy = '';
        if (user.CreatedBy?.includes('@')) {
          displayCreatedBy = user.CreatedBy.split('@')[0];
        } else {
          displayCreatedBy = user.CreatedBy || '';
        }

        // Handle ModifiedBy display  
        let displayModifiedBy = '';
        if (user.ModifiedBy) {
          if (user.ModifiedBy.includes('@')) {
            displayModifiedBy = user.ModifiedBy.split('@')[0];
          } else {
            displayModifiedBy = user.ModifiedBy;
          }
        } else {
          displayModifiedBy = displayCreatedBy;
        }

        return {
          userId: user.UserID,
          userName: user.UserName,
          userType: user.UserType,
          role: user.Role,
          division: user.Division,
          location: user.Location,
          bpCode: user.BPCode,
          emailId: user.EmailID,
          displayUserName: displayUserName,
          displayInsertedBy: displayCreatedBy,
          displayModifiedBy: displayModifiedBy,
          mobileNumber: user.MobileNumber,
          status: user.Status,
          insertedBy: user.CreatedBy,
          insertedOn: new Date(user.CreatedOn).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
          modifiedBy: user.ModifiedBy || user.CreatedBy,
          modifiedOn: user.ModifiedOn
            ? new Date(user.ModifiedOn).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
            : new Date(user.CreatedOn).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
          originalCreatedOn: user.CreatedOn,
          originalModifiedOn: user.ModifiedOn
        };
      }));

      setUsers(mappedUsers);
      return mappedUsers;
    } catch (error) {
      console.error('[UserMaster] GetUsers Error:', error);
      setApiMessage({ text: error.message || 'Failed to fetch users', type: 'error' });
      setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      user.role?.toLowerCase().includes(term) ||
      user.division?.toLowerCase().includes(term) ||
      user.location?.toLowerCase().includes(term) ||
      user.bpCode?.toLowerCase().includes(term) ||
      user.emailId?.toLowerCase().includes(term) ||
      user.mobileNumber?.toLowerCase().includes(term) ||
      user.userName?.toLowerCase().includes(term) ||
      user.displayUserName?.toLowerCase().includes(term)

    );
  });

  const totalRecords = filteredUsers.length;
  const totalPages = Math.ceil(totalRecords / pageSize) || 1;
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSort = (field) => {
    let direction = 'asc';
    if (sortField === field && sortDirection === 'asc') direction = 'desc';
    setSortField(field);
    setSortDirection(direction);
    const sorted = [...users].sort((a, b) => {
      const valA = (a[field] || '').toLowerCase();
      const valB = (b[field] || '').toLowerCase();
      if (valA < valB) return direction === 'asc' ? -1 : 1;
      if (valA > valB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setUsers(sorted);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const resetForm = () => {
    setFormData({ userName: '', userType: '', role: '', division: '', location: '', bpCode: '', emailId: '', mobileNumber: '', status: true });
    setMobileError('');
    setEmailError('');
  };

  const validateEmail = (email) => {
    if (!email) return '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(email.trim())) return 'Please enter a valid email address';
    return '';
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, emailId: value });
    setEmailError(validateEmail(value));
  };

  const validateMobileNumber = (mobile) => {
    if (!mobile) return '';
    if (!/^\d{10}$/.test(mobile)) {
      return 'Mobile number must be exactly 10 digits';
    }
    return '';
  };

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, mobileNumber: value });
    setMobileError(validateMobileNumber(value));
  };

  const handleView = (user) => {
    setModalMode('view');
    setFormData({
      userName: user.userName, userType: user.userType, role: user.role,
      division: user.division, location: user.location, bpCode: user.bpCode, emailId: user.emailId,
      mobileNumber: user.mobileNumber, status: user.status === 'Active'
    });
    setShowModal(true);
  };

  const handleEdit = (user, index) => {
    setModalMode('edit');
    setEditIndex(index);
    setFormData({
      userName: user.userName, userType: user.userType, role: user.role,
      division: user.division, location: user.location, bpCode: user.bpCode, emailId: user.emailId,
      mobileNumber: user.mobileNumber, status: user.status === 'Active'
    });
    setShowModal(true);
  };

  const handleDelete = async (user) => {
    if (window.confirm(`Are you sure you want to delete user "${user.userName}"?`)) {
      try {
        // Modified by Vinaya - await async payload builder (KMS encryption of mobile no.)
        const deletePayload = await buildDeleteUserPayload(user);

        await fetchApi('/api/UserMaster/DeleteUser', { method: 'POST', body: JSON.stringify(deletePayload) });

        await fetchApi('/api/AuditLog/CreateAuditLog', {
          method: 'POST',
          body: JSON.stringify(buildAuditLogPayload(deletePayload, 'UserMaster')),
          skipLoader: true
        }).catch(err => console.error('[UserMaster] CreateAuditLog Error:', err));

        await fetchUsers();
        setApiMessage({ text: 'User deleted successfully', type: 'success' });
        setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
      } catch (error) {
        console.error('[UserMaster] DeleteUser Error:', error);
        setApiMessage({ text: error.message || 'Failed to delete user', type: 'error' });
        setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
      }
    }
  };

  const handleStatusToggle = async (user, index) => {
    const newStatus = user.status === 'Active' ? 'Inactive' : 'Active';
    const updatedUsers = [...users];
    updatedUsers[index] = { ...updatedUsers[index], status: newStatus };
    setUsers(updatedUsers);

    try {
      const updatedUser = {
        userId: user.userId, userName: user.userName, userType: user.userType, role: user.role,
        division: user.division, location: user.location, bpCode: user.bpCode, emailId: user.emailId,
        mobileNumber: user.mobileNumber, status: newStatus === 'Active'
      };
      // Modified by Vinaya - await async payload builder (KMS encryption of mobile no.)
      const payload = await buildUpdateUserPayload(updatedUser, user);
      await fetchApi('/api/UserMaster/UpdateUser', { method: 'POST', body: JSON.stringify(payload) });

      await fetchApi('/api/AuditLog/CreateAuditLog', {
        method: 'POST',
        body: JSON.stringify(buildAuditLogPayload(payload, 'UserMaster')),
        skipLoader: true
      }).catch(err => console.error('[UserMaster] CreateAuditLog Error:', err));

      setApiMessage({ text: 'User status updated successfully', type: 'success' });
      setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      const revertedUsers = [...users];
      revertedUsers[index] = { ...revertedUsers[index], status: user.status };
      setUsers(revertedUsers);
      setApiMessage({ text: error.message || 'Failed to update status', type: 'error' });
      setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
    }
  };

  const handleViewHistory = async (user) => {
    try {
      const response = await fetchApi('/api/AuditLog/GetAuditLogs', {
        method: 'POST',
        body: JSON.stringify({ UserID: user.userId }),
      });

      let historyRecords = [];
      if (Array.isArray(response)) historyRecords = response;
      else if (response.data && Array.isArray(response.data)) historyRecords = response.data;
      else if (response.result && Array.isArray(response.result)) historyRecords = response.result;

      // Sort by ModifiedOn date (most recent first)
      const sortedHistoryRecords = historyRecords.sort((a, b) => {
        const dateA = new Date(a.ModifiedOn || a.CreatedOn);
        const dateB = new Date(b.ModifiedOn || b.CreatedOn);
        return dateB - dateA; // Descending order (latest first)
      });

      const mappedHistory = sortedHistoryRecords.map(item => ({
        status: item.Status,
        userId: item.UserID,
        // Handle encrypted UserName in history - don't try to decrypt, use email instead
        displayUserName: (kmsEncryption.isEncrypted(item.UserName)) 
          ? (item.EmailID?.includes('@') ? item.EmailID.split('@')[0] : 'Unknown User')
          : (item.EmailID?.includes('@') ? item.EmailID.split('@')[0] : item.UserName),
        userName: item.UserName,
        role: item.Role,
        division: item.Division,
        location: item.Location,
        createdBy: item.CreatedBy || '',
        createdOn: item.CreatedOn ? new Date(item.CreatedOn).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '',
        modifiedBy: item.ModifiedBy || item.CreatedBy || '',
        modifiedOn: item.ModifiedOn ? new Date(item.ModifiedOn).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '',
      }));

      setHistoryData(mappedHistory);
      setShowHistoryModal(true);
    } catch (error) {
      setApiMessage({ text: error.message || 'Failed to fetch audit history', type: 'error' });
      setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
    }
  };

  const handleHistoryDownload = () => {
    try {
      const excelData = historyData.map((item, index) => sanitizeDataObject({
        'S.No': index + 1,
        'Status': item.status,
        'User Name': item.displayUserName,
        'Role': item.role,
        'Division': item.division,
        'Location': item.location,
        'Created By': item.createdBy,
        'Created On': item.createdOn,
        'Modified By': item.modifiedBy,
        'Modified On': item.modifiedOn
      }));
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Audit History');
      XLSX.writeFile(workbook, `User_Audit_History_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('[UserMaster] History Download Error:', error);
    }
  };

  const handleDownload = () => {
    try {
      const excelData = users.map((user, index) => sanitizeDataObject({
        'S.No': index + 1,
        'Role': user.role,
        'Division': user.division,
        'Location': user.location,
        'BP Code': user.bpCode,
        'Email ID': user.emailId,
        'Mobile Number': user.mobileNumber,
        'Inserted By': user.displayInsertedBy,
        'Inserted On': user.insertedOn,
        'Modified By': user.displayModifiedBy,
        'Modified On': user.modifiedOn
      }));
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      worksheet['!cols'] = [{ wch: 8 }, { wch: 15 }, { wch: 12 }, { wch: 20 }, { wch: 12 }, { wch: 25 }, { wch: 15 }, { wch: 20 }, { wch: 18 }, { wch: 20 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(workbook, worksheet, 'User Master');
      const currentDate = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `User_Master_${currentDate}.xlsx`);
      setApiMessage({ text: 'Excel file downloaded successfully', type: 'success' });
      setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      console.error('[UserMaster] Excel Download Error:', error);
      setApiMessage({ text: 'Failed to download Excel file', type: 'error' });
      setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
    }
  };

  const handleSubmit = async () => {
    const errors = [];
    if (!formData.userType.trim()) errors.push('User Type');
    if (!formData.userName.trim()) errors.push('User Name');
    if (!formData.emailId.trim()) errors.push('Email ID');
    const emailValidationError = validateEmail(formData.emailId.trim());
    if (formData.emailId.trim() && emailValidationError) {
      setApiMessage({ text: emailValidationError, type: 'error' });
      setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
      return;
    }
    if (!formData.division.trim()) errors.push('Division');
    if (!formData.role.trim()) errors.push('Role');
    if (formData.userType !== 'Godrej Employee' && !formData.bpCode.trim()) errors.push('BP Code');
    if (errors.length > 0) {
      setApiMessage({ text: `Please fill mandatory fields: ${errors.join(', ')}`, type: 'error' });
      setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
      return;
    }
    // CSV Formula Injection validation
    const fieldsToCheck = ['userName', 'division', 'location', 'bpCode', 'emailId'];
    const injectionField = fieldsToCheck.find(f => containsFormulaInjection(formData[f]));
    if (injectionField) {
      setApiMessage({ text: `Invalid input: value cannot start with =, +, -, or @`, type: 'error' });
      setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
      return;
    }
    if (mobileError || (formData.mobileNumber && validateMobileNumber(formData.mobileNumber))) {
      setApiMessage({ text: 'Please fix mobile number validation errors', type: 'error' });
      setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
      return;
    }
    const duplicateEmail = users.find(u => 
      u.emailId?.toLowerCase() === formData.emailId.trim().toLowerCase() &&
      (modalMode === 'add' || users.indexOf(u) !== editIndex)
    );
    if (duplicateEmail) {
      setApiMessage({ text: `Email ID "${formData.emailId}" already exists`, type: 'error' });
      setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
      return;
    }

    
    
    if (modalMode === 'edit' && editIndex !== null) {
      try {
        const originalUser = users[editIndex];
        // Modified by Vinaya - await async payload builder (KMS encryption of mobile no.)
        const payload = await buildUpdateUserPayload(formData, originalUser);

        await fetchApi('/api/UserMaster/UpdateUser', { method: 'POST', body: JSON.stringify(payload) });

        await fetchApi('/api/AuditLog/CreateAuditLog', {
          method: 'POST',
          body: JSON.stringify(buildAuditLogPayload(payload, 'UserMaster')),
          skipLoader: true
        }).catch(err => console.error('[UserMaster] CreateAuditLog Error:', err));

        await fetchUsers();
        setApiMessage({ text: 'User updated successfully', type: 'success' });
        setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
      } catch (error) {
        console.error('[UserMaster] UpdateUser Error:', error);
        setApiMessage({ text: error.message || 'Failed to update user', type: 'error' });
        setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
      }
    } else {
      try {
        const payload = await buildCreateUserPayload(formData);
        const createResponse = await fetchApi('/api/UserMaster/CreateUser', { method: 'POST', body: JSON.stringify(payload) });

        // Fetch updated user list to get the real UserID assigned by the backend
        const freshUsers = await fetchUsers();

        // Resolve UserID: prefer API response, fallback to matching by email in refreshed list
        let newUserId = createResponse?.data?.UserID || createResponse?.data?.userId || createResponse?.UserID;
        if (!newUserId && freshUsers) {
          const matched = freshUsers.find(u => u.emailId?.toLowerCase() === formData.emailId.trim().toLowerCase());
          newUserId = matched?.userId;
        }

        const auditPayload = buildAuditLogPayload({ ...payload, UserID: newUserId }, 'UserMaster');
        await fetchApi('/api/AuditLog/CreateAuditLog', {
          method: 'POST',
          body: JSON.stringify(auditPayload),
          skipLoader: true
        }).catch(err => console.error('[UserMaster] CreateAuditLog Error:', err));

        setApiMessage({ text: 'User created successfully', type: 'success' });
        setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
      } catch (error) {
        console.error('[UserMaster] CreateUser Error:', error);
        setApiMessage({ text: error.message || 'Failed to create user', type: 'error' });
        setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
      }
    }
    closeModal();
  };

  const closeModal = () => {
    setShowModal(false);
    setModalMode('add');
    setEditIndex(null);
    resetForm();
  };

  return (
    <Header>
    <div className="master-container">
      {apiMessage.text && (
        <div className={`api-message ${apiMessage.type}`}>{apiMessage.text}</div>
      )}
      <div className="master-header">
        <h4>User Master</h4>
      </div>

      <div className="master-toolbar">
        <button className="btn-add-new" onClick={() => { setModalMode('add'); resetForm(); setShowModal(true); }}>+ Add New User</button>
        <div className="toolbar-right">
          <div className="search-wrapper">
            <span className="search-icon">&#128269;</span>
            <input type="text" className="search-input" placeholder="Search here..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
          <button className="btn-download" onClick={handleDownload} title="Download">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="master-table-wrapper">
        <table className="master-table">
          <thead>
            <tr>
              <th className="sortable-header" onClick={() => handleSort('status')}>Status {getSortIcon('status')}</th>
              <th>User Name</th>
              <th className="sortable-header" onClick={() => handleSort('role')}>Role {getSortIcon('role')}</th>
              <th className="sortable-header" onClick={() => handleSort('division')}>Division {getSortIcon('division')}</th>
              <th>Location</th>
              <th>BP Code</th>
              <th>Email ID</th>
              <th>Mobile Number</th>
              <th>Inserted By</th>
              <th>Inserted On</th>
              <th>Modified By</th>
              <th>Modified On</th>
              <th className="actions-header"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="13" className="no-data">Loading...</td></tr>
            ) : paginatedUsers.length > 0 ? paginatedUsers.map((user, index) => (
              <tr key={index}>
                <td>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={user.status === 'Active'}
                      onChange={() => handleStatusToggle(user, users.indexOf(user))}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="status-text" style={{ color: user.status === 'Active' ? 'green' : 'red', fontSize: '0.85em' }}>{user.status === 'Active' ? 'Active' : 'Inactive'}</span>
                </td>
                <td>{user.displayUserName}</td>
                <td>{user.role}</td>
                <td>{user.division}</td>
                <td>{user.location}</td>
                <td>{user.bpCode}</td>
                <td>{user.emailId?.includes('@') ? '*****@' + user.emailId.split('@')[1] : (user.emailId || '')}</td>
                <td>{user.mobileNumber ? '******' + user.mobileNumber.slice(-4) : ''}</td>
                <td>{user.displayInsertedBy}</td>
                <td>{user.insertedOn}</td>
                <td>{user.displayModifiedBy}</td>
                <td>{user.modifiedOn}</td>
                <td className="actions-cell">
                  <button className="action-btn" onClick={() => handleView(user)} title="View">
                    <img src={eyeIcon} alt="View" />
                  </button>
                  <button className="action-btn" onClick={() => handleEdit(user, users.indexOf(user))} title="Edit">
                    <img src={editIcon} alt="Edit" />
                  </button>
                  <button className="action-btn" onClick={() => handleViewHistory(user)} data-tooltip="View History">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 12a9 9 0 1 1 0 .01"/>
                      <path d="M3 4v5h5"/>
                      <polyline points="12 7 12 12 15 15"/>
                    </svg>
                  </button>
                </td>
              </tr>
            )) : (
              <tr><td colSpan="13" className="no-data">No records found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="master-pagination">
        <span>{((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords}</span>
        <div className="pagination-controls">
          <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>&#x27E8;&#x27E8;</button>
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>&#x27E8;</button>
          <span>Page {currentPage} of {totalPages}</span>
          <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>&#x27E9;</button>
          <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>&#x27E9;&#x27E9;</button>
        </div>
      </div>

      {/* History Modal */}
      {showHistoryModal && (
        <div className="modal-overlay">
          <div className="modal-content history-modal">
            <div className="modal-header">
              <h5>View History</h5>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button className="btn-download" onClick={handleHistoryDownload} title="Download History">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                </button>
                <div className="modal-close" onClick={() => setShowHistoryModal(false)}>&times;</div>
              </div>
            </div>
            <div className="modal-body">
              <div className="master-table-wrapper">
                <table className="master-table history-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>User Name</th>
                      <th>Role</th>
                      <th>Division</th>
                      <th>Location</th>
                      <th>Created By</th>
                      <th>Created On</th>
                      <th>Modified By</th>
                      <th>Modified On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.map((item, index) => (
                      <tr key={index}>
                        <td>{item.status}</td>
                        <td>{item.displayUserName}</td>
                        <td>{item.role}</td>
                        <td>{item.division}</td>
                        <td>{item.location}</td>
                        <td>{item.createdBy}</td>
                        <td>{item.createdOn}</td>
                        <td>{item.modifiedBy}</td>
                        <td>{item.modifiedOn}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content user-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5>{modalMode === 'edit' ? 'Edit User' : modalMode === 'view' ? 'View User' : 'Add User'}</h5>
              <div className="modal-close" onClick={closeModal}>&times;</div>
            </div>

            <div className="modal-body">
              {/* Row 1: User Type and User Name */}
              <div className="modal-form-row">
                <div className="modal-field">
                  <label className="modal-field-label">User Type <span style={{color:'red'}}>*</span></label>
                  {modalMode === 'add' ? (
                    <select value={formData.userType} onChange={(e) => setFormData({ ...formData, userType: e.target.value })} className="modal-select" disabled={modalMode === 'view'}>
                      <option value="">Select User Type</option>
                      {userTypeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  ) : (
                    <span className="user-type-display">{formData.userType}</span>
                  )}
                </div>
                <div className="modal-field">
                  <label className="modal-field-label">User Name <span style={{color:'red'}}>*</span></label>
                  <input type="text" placeholder="User Name" value={formData.userName} onChange={(e) => setFormData({ ...formData, userName: e.target.value })} disabled={modalMode === 'view'} />
                </div>
              </div>

              {/* Row 2: Division, Location, Role */}
              <div className="modal-form-row">
                <div className="modal-field">
                  <label className="modal-field-label">Division <span style={{color:'red'}}>*</span></label>
                  <input type="text" placeholder="Division" value={formData.division} onChange={(e) => setFormData({ ...formData, division: e.target.value })} disabled={modalMode === 'view'} />
                </div>
                <div className="modal-field">
                  <label className="modal-field-label">Location</label>
                  <input type="text" placeholder="Location" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} disabled={modalMode === 'view'} />
                </div>
                <div className="modal-field">
                  <label className="modal-field-label">Role <span style={{color:'red'}}>*</span></label>
                  <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} className="modal-select" disabled={modalMode === 'view'}>
                    <option value="">Select Role</option>
                    {roleOptions.map(opt => <option key={opt.code} value={opt.code}>{opt.description}</option>)}
                  </select>
                </div>
              </div>

              {/* Row 3: BP Code, Email ID, Mobile Number */}
              <div className="modal-form-row">
                <div className="modal-field">
                  <label className="modal-field-label">BP Code {formData.userType !== 'Godrej Employee' && <span style={{color:'red'}}>*</span>}</label>
                  <input type="text" placeholder="BP Code" value={formData.bpCode} onChange={(e) => setFormData({ ...formData, bpCode: e.target.value })} disabled={modalMode === 'view'} />
                </div>
                <div className="modal-field">
                  <label className="modal-field-label">Email ID <span style={{color:'red'}}>*</span></label>
                  <input type="text" placeholder="Email ID" value={formData.emailId} onChange={handleEmailChange} disabled={modalMode === 'view'} />
                  {emailError && <span className="error-text" style={{color: 'red', fontSize: '12px'}}>{emailError}</span>}
                </div>
                <div className="modal-field">
                  <label className="modal-field-label">Mobile Number</label>
                  <input 
                    type="text" 
                    placeholder="Mobile Number" 
                    value={formData.mobileNumber} 
                    onChange={handleMobileChange}
                    disabled={modalMode === 'view'}
                    maxLength="10"
                  />
                  {mobileError && <span className="error-text" style={{color: 'red', fontSize: '12px'}}>{mobileError}</span>}
                </div>
              </div>

              {/* Row 4: Status */}
              <div className="modal-form-row">
                <div className="modal-field status-field">
                  <span className="status-label">Status</span>
                  <label className="toggle-switch">
                    <input type="checkbox" checked={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.checked })} disabled={modalMode === 'view'} />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="status-text">{formData.status ? 'Active' : 'Inactive'}</span>
                </div>
              </div>

              {/* Footer buttons */}
              <div className="modal-footer user-modal-footer" style={{ gap: '20px' }}>
                {modalMode !== 'view' && (
                  <>
                    {modalMode === 'add' && <button className="btn-add-role" onClick={resetForm}>Reset</button>}
                    <button className="btn-add-role" onClick={handleSubmit}>{modalMode === 'edit' ? 'Update' : 'Add'}</button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </Header>
  );
};

export default UserMaster;
